import type { SessionState } from '@/versions/v1/schema/scenario';
import { applyFmeaReviews, type AdvisorySessionState } from '@/versions/v1/shared/AdvisoryEngine';
import { CASE_OUTCOMES, CASES, FMEA_UPDATE_REVIEWS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { FMEAUpdateReview, ProposedChangeKind } from '@/versions/v1/schema/case';
import { ORE_ZONE_SCHEDULE, oreZoneAt } from '@/versions/v1/shared/bunker-hill-ore-zones';
import { RULE_SET_VERSIONS } from '@/versions/v1/shared/bunker-hill-rule-claims';
import { liveRuleSet } from '@/versions/v1/shared/RuleSetEngine';
import { addMinutes, diffMinutes, productionDayOf } from '@/versions/v1/shared/clock.derive';
import { readingComparisonBasis } from '@/versions/v1/shared/comparison-basis';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { windowMean } from '@/versions/v1/shared/series.derive';
import type { PerformanceKpi } from './Performance.transform';
import { deviationStatusPct, deviationToTone } from './Performance.transform';
import type { TrendMarker, VizTone } from '@/versions/v1/modules/_viz';
import { formatDayMonth } from '@/versions/v1/shared/clock.derive';
import { formatPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { BUNKER_HILL_ASSIGNMENTS, BUNKER_HILL_PERSONS } from '@/versions/v1/shared/bunker-hill-org-seed';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import {
  formatExposurePerDay,
  openCaseExposurePerDay,
  pricedDriverTree,
} from '@/versions/v1/modules/advisory/DriverTree/Scene.transform';
import { gmAtStakeKpi, type GmMiniItem, type LandingStripCell } from './GmLeadBand.transform';
import { issueCaseId, issueListRows } from './IssueList.transform';
import { sevenDaySeriesDelta, seniorMetTrendChart } from './LandingSeries.transform';
import {
  SMET_COMPETING_HYPOTHESES,
  SMET_LEADING_CAUSE,
  seniorMetKbKpis,
  seniorMetSeatReviews,
  seniorMetTrendKpis,
  type TabKpiTile,
} from './RoleLandingTabKpis.transform';
import {
  cascadeRows,
  kbSuggestions,
  processHealthCards,
  type HealthCardModel,
  type HealthMiniRow,
} from './SeniorMetTabs.transform';

export type StewardshipReviewRow = {
  id: string;
  summary: string;
  href: string;
};

export type ProposedRuleRow = {
  id: string;
  summary: string;
  provenance: string;
  href: string;
};

export const STEWARDSHIP_FIRST_PAINT_COUNT = 3;

export function stewardshipVisibleRows<T>(rows: readonly T[], expanded: boolean): readonly T[] {
  return expanded ? rows : rows.slice(0, STEWARDSHIP_FIRST_PAINT_COUNT);
}

export function stewardshipHiddenCount(rows: readonly unknown[], expanded: boolean): number {
  if (expanded) return 0;
  return Math.max(0, rows.length - STEWARDSHIP_FIRST_PAINT_COUNT);
}

export type SeniorMetLandingModel = {
  title: string;
  kpis: PerformanceKpi[];
  ruleSetVersionLine: string;
  pendingReviewCount: number;
  stewardshipReviews: readonly StewardshipReviewRow[];
  proposedRows: readonly ProposedRuleRow[];
  zoneLabel: string;
  zoneDays: number;
};

function daysOnCurrentZone(cursor: string): number {
  const zone = oreZoneAt(cursor);
  if (!zone) return 0;
  const entry = ORE_ZONE_SCHEDULE.find((row) => row.zoneId === zone.id);
  if (!entry) return 0;
  const { date: cursorDate } = productionDayOf(cursor);
  const { date: fromDate } = productionDayOf(entry.from);
  const cursorMid = `${cursorDate}T12:00`;
  const fromMid = `${fromDate}T12:00`;
  const days = Math.floor(diffMinutes(cursorMid, fromMid) / (24 * 60)) + 1;
  return Math.max(1, days);
}

function pbRecoverySevenDayMean(state: SessionState): { mean: number; caption: string; tone: PerformanceKpi['tone'] } {
  const from = addMinutes(state.cursor, -7 * 24 * 60);
  const mean = windowMean('rs-pb-rec', state, from);
  const basis = readingComparisonBasis(readingByIdLookup('rd-kpi-pb-rec')!);
  if (!basis) {
    return { mean, caption: '7-day mean —', tone: 'neutral' };
  }
  const gapPt = mean - basis.value;
  const sign = gapPt >= 0 ? '+' : '−';
  let caption = `7-day mean vs ${basis.label} ${basis.value.toFixed(1)}% · ${sign}${Math.abs(gapPt).toFixed(1)} pt`;
  if (basis.design !== undefined && basis.outranked.includes('design') && basis.design !== basis.value) {
    const designGap = mean - basis.design;
    const dSign = designGap >= 0 ? '+' : '−';
    caption += ` · design ${basis.design.toFixed(1)}% · ${dSign}${Math.abs(designGap).toFixed(1)}%`;
  }
  return {
    mean,
    caption,
    tone: deviationToTone(deviationStatusPct(mean, basis.value)),
  };
}

function proposedRowsFromSeed(projectSlug: string): ProposedRuleRow[] {
  const rows: ProposedRuleRow[] = [];
  for (const review of FMEA_UPDATE_REVIEWS) {
    if (review.reviewStatus !== 'approved' || review.approvalStatus !== 'pending') continue;
    if (!review.reviewSeats.includes('senior-metallurgist')) continue;
    rows.push({
      id: review.id,
      summary: review.proposedChange.summary,
      provenance: review.id,
      href: `/projects/${projectSlug}/advisory?tab=approvals`,
    });
  }
  for (const outcome of CASE_OUTCOMES) {
    if (!outcome.proposedRuleId) continue;
    if (!outcome.evidence?.toLowerCase().includes('signal')) continue;
    rows.push({
      id: outcome.id,
      summary: outcome.diagnosedCause,
      provenance: outcome.caseId,
      href: `/projects/${projectSlug}/advisory?tab=approvals`,
    });
  }
  return rows;
}

/** The review's deep link into Advisory › Approvals, opened on that review. */
function reviewHref(projectSlug: string, reviewId: string): string {
  return `/projects/${projectSlug}/advisory?tab=approvals&review=${reviewId}`;
}

/** The FMEA reviews waiting on the seat, with the session's reviews applied. */
function seatReviews(advisory: AdvisorySessionState): FMEAUpdateReview[] {
  return seniorMetSeatReviews(applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory));
}

function stewardshipReviews(advisory: AdvisorySessionState, projectSlug: string): StewardshipReviewRow[] {
  return seatReviews(advisory).map((row) => ({
    id: row.id,
    summary: row.proposedChange.summary,
    href: reviewHref(projectSlug, row.id),
  }));
}

export function seniorMetLandingModel(
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
): SeniorMetLandingModel {
  const live = liveRuleSet(advisory);
  const seededVersion = RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!;
  const version = live.version.n >= seededVersion.n ? live.version : seededVersion;
  const pendingSeniorReviews = seatReviews(advisory).length;
  const proposedRows = proposedRowsFromSeed(projectSlug);
  const pb7 = pbRecoverySevenDayMean(state);
  const zone = oreZoneAt(state.cursor);
  const zoneDays = daysOnCurrentZone(state.cursor);

  return {
    title: 'Rule-set stewardship · operating record',
    kpis: [
      {
        label: 'Rule set in force',
        value: `v${version.n}`,
        caption: `${pendingSeniorReviews} FMEA review${pendingSeniorReviews === 1 ? '' : 's'} pending · ${proposedRows.length} proposed row${proposedRows.length === 1 ? '' : 's'} awaiting decision`,
        tone: pendingSeniorReviews > 0 || proposedRows.length > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Proposed rows awaiting decision',
        value: String(proposedRows.length),
        caption: `${proposedRows.length} in stewardship panel below`,
        tone: proposedRows.length > 0 ? 'warn' : 'ok',
      },
      {
        label: 'Pb recovery — 7-day mean',
        value: `${pb7.mean.toFixed(1)}%`,
        caption: pb7.caption,
        tone: pb7.tone,
      },
      {
        label: 'Current ore zone',
        value: zone?.label ?? '—',
        caption: zone ? `${zoneDays} day${zoneDays === 1 ? '' : 's'} on this zone` : '—',
        tone: 'info',
      },
    ],
    ruleSetVersionLine: `Rule set v${version.n} in force · accepted ${version.acceptedAt.split('T')[0] ?? version.acceptedAt}`,
    pendingReviewCount: pendingSeniorReviews,
    stewardshipReviews: stewardshipReviews(advisory, projectSlug),
    proposedRows,
    zoneLabel: zone?.label ?? '—',
    zoneDays,
  };
}

// ---- The landing's composition (polish.P27 #714) ----------------------------
// Locked comp `.impeccable/mocks/decision/landing-senior-met.html` A, finished
// with B's sign-off card and headroom meters and C's two header facts. Every
// figure below is read from the transforms and engines the four views read on
// dev; this layer only re-shapes them for the strip-as-tabs family.

export type SeniorMetTabId = 'health' | 'trends' | 'diag' | 'kb';

export type SmetHeaderFact = { id: 'rule-set' | 'ore-zone'; lead: string; rest: string };

/** Comp C's two header facts, from `seniorMetLandingModel` (which computes them
 *  and, on dev, rendered none of its four KPIs). */
export function smetHeaderFacts(
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
): SmetHeaderFact[] {
  const model = seniorMetLandingModel(state, advisory, projectSlug);
  const version = liveRuleSetVersion(advisory);
  return [
    { id: 'rule-set', lead: `Rule set v${version.n}`, rest: ` · accepted ${formatDayMonth(version.acceptedAt)}` },
    { id: 'ore-zone', lead: model.zoneLabel, rest: ` · day ${model.zoneDays}` },
  ];
}

function liveRuleSetVersion(advisory: AdvisorySessionState) {
  const live = liveRuleSet(advisory);
  const seededVersion = RULE_SET_VERSIONS[RULE_SET_VERSIONS.length - 1]!;
  return live.version.n >= seededVersion.n ? live.version : seededVersion;
}

/** `Rule set v7 in force · accepted 08 Jun` — the stewardship card's first line. */
export function smetRuleSetLine(advisory: AdvisorySessionState): { lead: string; rest: string } {
  const version = liveRuleSetVersion(advisory);
  return { lead: `Rule set v${version.n}`, rest: ` in force · accepted ${formatDayMonth(version.acceptedAt)}` };
}

/** Status walls are quiet: only an exception keeps its colour. */
function quietTone(tone: VizTone): VizTone {
  return tone === 'warn' || tone === 'bad' ? tone : 'neutral';
}

const AREA_PILL: Record<HealthCardModel['tone'], { tone: 'ok' | 'warn' | 'bad'; label: string }> = {
  ok: { tone: 'ok', label: 'OK' },
  warn: { tone: 'warn', label: 'Watch' },
  bad: { tone: 'bad', label: 'Alert' },
};

/** Comp adjudications 1 and 2: grades and tails render once, in the concentrate
 *  card, against the concentrate spec and the mass balance, so the area blocks
 *  drop their grade and tail-assay rows. */
const ROWS_OWNED_BY_CONCENTRATE_CARD = new Set(['Pb grade (%)', 'Zn grade (%)', 'Tail Zn (%)', 'Tail Pb (%)']);

export type SmetAreaBlock = {
  id: string;
  area: string;
  pill: { tone: 'ok' | 'warn' | 'bad'; label: string };
  tag?: string;
  rows: readonly HealthMiniRow[];
};

export function smetAreaBlocks(state: SessionState): SmetAreaBlock[] {
  return processHealthCards(state).map((card) => ({
    id: card.id,
    area: card.area,
    pill: AREA_PILL[card.tone],
    tag: card.tag,
    rows: card.rows.filter((row) => !ROWS_OWNED_BY_CONCENTRATE_CARD.has(row.metric)),
  }));
}

/** The at-stake link: the GM landing's engine value (adjudications 4 and 11),
 *  its split and the open cases the Diagnostic cases view ranks. */
export type SmetAtStake = {
  label: string;
  figure: string;
  unit: string;
  comparisonLead: string;
  comparisonRest: string;
};

export function smetAtStake(state: SessionState, projectSlug: string): SmetAtStake {
  const gm = gmAtStakeKpi(state);
  const exposure = openCaseExposurePerDay(pricedDriverTree(DRIVER_TREE_SEED, state), state.cursor);
  // A zero part (no accruing penalty at the cursor) is absence, not content.
  const rest = ([['risk', exposure.risk], ['accruing penalty', exposure.accruingPenalty]] as const)
    .filter(([, amount]) => amount > 0)
    .map(([label, amount]) => `${formatExposurePerDay(amount)} ${label}`);
  const cases = issueListRows('smet', projectSlug, state).length;
  return {
    label: gm.label,
    figure: gm.figure,
    unit: gm.unit,
    comparisonLead: formatExposurePerDay(exposure.activeLoss),
    comparisonRest: ` active loss · ${[...rest, `${cases} open cases`].join(' · ')}`,
  };
}

/** The four strip cells: each view's headline, said once (adjudication 3). */
export function smetStripCells(
  state: SessionState,
  advisory: AdvisorySessionState,
  projectSlug: string,
): LandingStripCell<SeniorMetTabId>[] {
  const cards = processHealthCards(state);
  const off = cards.filter((card) => card.tone !== 'ok');
  const worst = cards.find((card) => card.tone === 'bad') ?? off[0];
  const zn = sevenDaySeriesDelta('rs-zn-rec', state);
  const suggestionsForReview = kbSuggestions().filter((row) => row.statusTone !== 'ok').length;
  const model = seniorMetLandingModel(state, advisory, projectSlug);
  const holder = seniorMetHolderName();
  return [
    {
      id: 'health',
      name: 'Process health',
      figure: String(off.length),
      figureContext: `of ${cards.length} areas off`,
      comparisonLead: worst ? `${sentenceCase(worst.area)}` : '',
      comparisonRest: worst ? ` ${AREA_PILL[worst.tone].label.toLowerCase()}${worst.tag ? ` · ${worst.tag}` : ''}` : '',
      actor: 'Engine',
    },
    {
      id: 'trends',
      name: '7-day trend analysis',
      figure: formatPtDelta(zn.delta),
      figureContext: 'Zn rec',
      comparisonLead: `from ${zn.first.toFixed(1)}%`,
      comparisonRest: ' · steepest decline mid-week',
      actor: 'Engine',
    },
    {
      id: 'diag',
      name: 'Diagnostic cases',
      figure: `${SMET_LEADING_CAUSE.confidencePct}%`,
      figureContext: SMET_LEADING_CAUSE.label.toLowerCase(),
      comparisonLead: 'leading cause',
      comparisonRest: ` · ${SMET_COMPETING_HYPOTHESES.length + 1} competing hypotheses`,
      actor: 'Decision model',
    },
    {
      id: 'kb',
      name: 'KB update suggestions',
      figure: String(suggestionsForReview),
      figureContext: 'for review',
      comparisonLead: `${model.pendingReviewCount} FMEA reviews`,
      comparisonRest: ' at your seat',
      actor: 'Agent',
      moreActors: [{ actor: 'Human', label: holder }],
    },
  ];
}

/** `Zn Flotation` → `Zn flotation` (the strip's comparison reads as a phrase). */
function sentenceCase(area: string): string {
  const [first, ...rest] = area.split(' ');
  return [first, ...rest.map((word) => word.toLowerCase())].join(' ');
}

/** The person in the Senior metallurgist seat (`seat-senior-met`, L. Chen). */
export function seniorMetHolderName(): string {
  const assignment = BUNKER_HILL_ASSIGNMENTS.find((row) => row.seatId === 'seat-senior-met');
  return BUNKER_HILL_PERSONS.find((row) => row.id === assignment?.personId)?.name ?? 'Senior met';
}

function miniFromTile(tile: TabKpiTile): GmMiniItem {
  return { id: tile.id, label: tile.label, value: tile.value, caption: tile.caption, tone: quietTone(tile.tone) };
}

/** 7-day trend analysis: throughput, pH and Pb recovery (the Zn change leads the strip). */
export function smetTrendMiniRow(state: SessionState): GmMiniItem[] {
  return seniorMetTrendKpis(state).filter((tile) => tile.id !== 'smet-tr-1').map(miniFromTile);
}

/** KB update suggestions: approvals, FMEA rows and knowledge-base health (the
 *  count for review leads the strip). */
export function smetKbMiniRow(state: SessionState): GmMiniItem[] {
  return seniorMetKbKpis(state).filter((tile) => tile.id !== 'smet-kb-1').map(miniFromTile);
}

/** One FMEA review waiting on the seat (polish.P27 r1): the proposed change,
 *  its id and kind, and the link that opens it in Advisory › Approvals. */
export type SmetSignOff = {
  id: string;
  summary: string;
  kindLabel: string;
  href: string;
};

const CHANGE_KIND_LABEL: Record<ProposedChangeKind, string> = {
  'new-row': 'New FMEA row',
  'narrower-condition': 'Narrower condition',
  'adjusted-likelihood': 'Adjusted likelihood',
  'band-change': 'Band change',
};

/** B's sign-off card, read from what waits on `seat-senior-met` at the cursor:
 *  the same FMEA reviews the strip counts (`FMEA reviews at your seat`). The
 *  SMBS dose is the Superintendent's gate and the CuSO4 batch is the Control
 *  metallurgist's action, so neither is listed here (discrepancies row 318). */
export function smetSignOffs(advisory: AdvisorySessionState, projectSlug: string): SmetSignOff[] {
  return seatReviews(advisory).map((review) => ({
    id: review.id,
    summary: review.proposedChange.summary,
    kindLabel: CHANGE_KIND_LABEL[review.proposedChange.kind],
    href: reviewHref(projectSlug, review.id),
  }));
}

/** A review the seat has already taken that now sits with the approver. */
export type SmetReviewedRow = {
  id: string;
  title: string;
  /** `FMR-0007 · reviewed 09:15 · with the GM`. */
  status: string;
  /** Why it matters to this landing (adjudication 8's pathway sentence). */
  pathway: string;
  caseId: string;
};

/** The SMBS carry-over signature behind the Pb in Zn issue (adjudication 8):
 *  reviewed at this seat and now with the approver, in a done state — never
 *  as waiting on the Senior metallurgist. */
export function smetReviewedWithApprover(advisory: AdvisorySessionState, state: SessionState): SmetReviewedRow | null {
  const caseId = issueCaseId('iss-pb-in-zn-con');
  if (!caseId) return null;
  const outcomeIds = new Set(CASE_OUTCOMES.filter((row) => row.caseId === caseId).map((row) => row.id));
  const review = applyFmeaReviews(FMEA_UPDATE_REVIEWS, advisory).find(
    (row) =>
      row.caseOutcomeId !== undefined &&
      outcomeIds.has(row.caseOutcomeId) &&
      row.reviewSeats.includes('senior-metallurgist') &&
      row.reviewStatus === 'approved',
  );
  if (!review) return null;
  const caseTitle = CASES.find((row) => row.id === caseId)?.title ?? review.proposedChange.summary;
  const approver = review.approvalLabel ?? 'GM';
  const stage =
    review.approvalStatus === 'approved' ? 'approved' : review.approvalStatus === 'rejected' ? 'rejected' : `with the ${approver}`;
  return {
    id: review.id,
    title: `${caseTitle} signature`,
    status: `${review.id} · reviewed ${reviewStamp(review.reviewedAt, state.cursor)} · ${stage}`,
    pathway: 'Shares the SMBS pathway with the Zn recovery shortfall',
    caseId,
  };
}

/** `09:15` on the cursor's day, else `07 Jun 09:15`. */
function reviewStamp(at: string | undefined, cursor: string): string {
  if (!at) return '—';
  const time = at.includes('T') ? at.split('T')[1]!.slice(0, 5) : at;
  if (!at.includes('T') || at.slice(0, 10) === cursor.slice(0, 10)) return time;
  return `${formatDayMonth(at)} ${time}`;
}

/** The area card's meta: the cursor's clock time and what each reading is read against. */
export function smetHealthMeta(state: SessionState): string {
  const time = state.cursor.includes('T') ? state.cursor.split('T')[1]!.slice(0, 5) : state.cursor;
  return `at ${time} · vs design, band or limit`;
}

export type SmetHypothesis = { id: string; label: string; versus?: string; figure: string; strong: boolean };

/** Competing hypotheses: the two pairs still open, then the leading cause and its confidence. */
export function smetHypotheses(): SmetHypothesis[] {
  return [
    ...SMET_COMPETING_HYPOTHESES.map((row, index) => ({
      id: `hyp-${index + 1}`,
      label: row.a,
      versus: row.b,
      figure: `on ${row.on}`,
      strong: false,
    })),
    {
      id: 'hyp-lead',
      label: SMET_LEADING_CAUSE.label,
      figure: `${SMET_LEADING_CAUSE.confidencePct}%`,
      strong: true,
    },
  ];
}

/** Numbered chart markers on the Zn line, one per cascade step (adjudication 5). */
export function smetCascadeMarkers(state: SessionState): TrendMarker[] {
  const chart = seniorMetTrendChart(state);
  const zn = chart.series[0]!;
  return cascadeRows(state).map((row, index) => ({
    index: row.markerIndex,
    value: zn.points[row.markerIndex] ?? 0,
    label: String(index + 1),
    tone: 'neutral',
  }));
}
